import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { MapPin, Phone } from "lucide-react";
import ShippingProofForm from "@/components/ShippingProofForm";

export default function SellerSales() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch all paid orders where any item.seller_id === user.user_id
    // We use /orders/mine (buyer) is wrong. Use the seller side view: pull only paid orders that include seller.
    // Server /admin/shipping-oversight lists paid orders but admin-only.
    // Simplest: fetch via /orders/all won't work either. Let's create a helper on client: use dispute/ship endpoints per order.
    // Actually: for a seller we don't have a dedicated endpoint. Add temporary: filter /orders/all if admin, else use /seller/sales
    api.get("/seller/sales")
      .then(({ data }) => setOrders(data))
      .catch((err) => console.warn("seller sales load failed", err))
      .finally(() => setLoading(false));
  }, [user]);

  if (loading) return <p className="font-mono-b p-6" data-testid="seller-sales-loading">Loading your sales...</p>;

  if (orders.length === 0) {
    return (
      <div className="border-2 border-dashed border-black p-12 text-center font-mono-b" data-testid="seller-sales-empty">
        No sales yet. When someone buys your item, it'll show up here for shipping.
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="seller-sales">
      <div className="border-2 border-black bg-[#D4FF00] p-4">
        <p className="font-display text-2xl">SHIP + GET PAID</p>
        <p className="font-mono-b text-xs mt-1">Every sale is held in escrow with OUTLAW-D. Pack the item, upload a photo + AWB below, and admin releases your payout usually within 24 hours.</p>
      </div>
      {orders.map((o) => (
        <div key={o.order_id} className="bg-white border-2 border-black brutal-shadow-sm p-4" data-testid={`sale-${o.order_id}`}>
          <div className="flex justify-between flex-wrap gap-2 mb-3">
            <div>
              <p className="font-mono-b text-xs text-black/60">{o.order_id}</p>
              <p className="font-display text-xl">₹{o.your_amount ?? o.total} · <span className={o.status === "shipped" ? "text-green-700" : "text-[#FF007F]"}>{o.status.toUpperCase()}</span></p>
            </div>
            <p className="font-mono-b text-xs">{new Date(o.created_at).toLocaleString()}</p>
          </div>

          {/* SHIPPING ADDRESS — required to ship the item */}
          {o.address && (
            <div className="mb-3 border-2 border-black bg-[#D4FF00] p-3" data-testid={`sale-address-${o.order_id}`}>
              <p className="font-mono-b text-[10px] font-bold uppercase flex items-center gap-1"><MapPin className="w-3 h-3" /> SHIP TO</p>
              <p className="font-display text-2xl leading-tight mt-1">{o.address.name}</p>
              <p className="font-mono-b text-sm mt-1">{o.address.line1}</p>
              <p className="font-mono-b text-sm">{o.address.city}, {o.address.state} — {o.address.pincode}</p>
              <p className="font-mono-b text-sm mt-1 inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {o.address.phone}</p>
            </div>
          )}
          <div className="flex flex-wrap gap-2 mb-3">
            {(o.your_items || o.items).map((it) => (
              <div key={`${it.product_id}-${it.size}`} className="flex gap-2 items-center border-2 border-black bg-[#F4F4F0] p-2">
                {it.image && <img src={it.image} alt="" className="w-12 h-12 object-cover" />}
                <div className="font-mono-b text-xs"><b>{it.name}</b><br/>×{it.quantity} {it.size ? `· ${it.size}` : ""} · ₹{it.subtotal}</div>
              </div>
            ))}
          </div>
          <ShippingProofForm order={o} onSaved={() => window.location.reload()} />
        </div>
      ))}
    </div>
  );
}
